import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import Button from '../../components/ui/Button';
import A11yDialog, { DialogErrorLine } from '../../components/ui/A11yDialog';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 30px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  margin-bottom: 14px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

// Maaş alanının ALTINDAKİ bant satırı (FE-19). Bandı teklif YAZILMADAN ÖNCE göstermek bu
// partinin ana ürün maddesi: eskiden işe alımcı tutarı körlemesine giriyor, bant ihlalini
// ancak onay ekranında devre dışı kalmış bir butonun HTML `title` ipucundan (dokunmatik
// cihazda görünmez) öğreniyordu.
const BandLine = styled.p`
  margin: -8px 0 14px;
  font-size: 12.5px;
  color: ${({ $tone, theme }) => ($tone === 'warn' ? '#B45309' : theme.colors.text.dark.little)};
  background: ${({ $tone }) => ($tone === 'warn' ? '#FFFBEB' : 'transparent')};
  border-left: ${({ $tone }) => ($tone === 'warn' ? '3px solid #F59E0B' : 'none')};
  padding: ${({ $tone }) => ($tone === 'warn' ? '7px 10px' : '0')};
  border-radius: 4px;
  line-height: 1.5;
`;

const CURRENCIES = [
  { id: 'TRY', name: '₺ TRY' },
  { id: 'USD', name: '$ USD' },
  { id: 'EUR', name: '€ EUR' },
  { id: 'GBP', name: '£ GBP' },
];

// FE-34 — küme KADRO TALEBİNİNKİYLE birebir aynı olmalı (ats-requisitions/config
// CONTRACT_TYPES ve backend AtsOfferDataAccess.AllowedContractTypes). `permanent_part_time`
// eksikti: yarı zamanlı bir kadro teklife dönüşürken sözleşme tipi sessizce kayboluyordu.
const CONTRACT_TYPES_KEYS = [
  'permanent_full_time',
  'permanent_part_time',
  'fixed_term',
  'contractor',
  'internship',
];

// Tutar biçimi: yalnız BASAMAK AYIRICI ekler; kuruşu YUVARLAMAZ, değeri DEĞİŞTİRMEZ
// (EO-07'nin `N0` kusuruyla aynı sınıf — gösterilen sayı girilen sayıdan farklı olamaz).
const formatAmount = (value, lang) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  return n.toLocaleString(lang || 'tr', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// ISO tarih damgasını DateTimePicker'ın beklediği "yyyy-MM-dd"ye indirir; saat dilimi
// kaydırması yapmaz (damganın tarih parçası olduğu gibi alınır).
const toDateInput = (value) => (typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : '');

// `mode`:
//   'create'  → POST /v1/ats-offers          (yeni teklif)
//   'revise'  → POST /v1/ats-offers/{id}/revise (FE-33: yeni SÜRÜM; önceki satır
//               `negotiating`e arşivlenir). Form AYNI form — bant gösterimi, beyaz listeler
//               ve tarih kuralları tek kopya kalsın diye ikinci bir ekran YAZILMADI.
export default function OfferCreateDialog({
  open, application, onClose, onCreated,
  mode = 'create', offerId = null, initialOffer = null,
}) {
  const { t, i18n } = useTranslation();
  const isRevise = mode === 'revise';
  // Kadro talebinin maaş bandı + sözleşme tipi / para birimi (FE-19 ön doldurma).
  // `null` = henüz çekilmedi ya da çekilemedi — bant satırı hiç çizilmez (yalan söylemez).
  const [requisition, setRequisition] = useState(null);
  const [form, setForm] = useState(() => ({
    baseSalary: initialOffer?.baseSalary != null ? String(initialOffer.baseSalary) : '',
    currency: initialOffer?.currency || 'TRY',
    bonusCash: initialOffer?.bonusCash != null ? String(initialOffer.bonusCash) : '',
    bonusEquity: initialOffer?.bonusEquity != null ? String(initialOffer.bonusEquity) : '',
    startDate: toDateInput(initialOffer?.startDate),
    responseDeadline: toDateInput(initialOffer?.responseDeadline),
    contractType: CONTRACT_TYPES_KEYS.includes(initialOffer?.contractType)
      ? initialOffer.contractType : 'permanent_full_time',
    contractDurationMonths: initialOffer?.contractDurationMonths != null
      ? String(initialOffer.contractDurationMonths) : '',
    benefitsJson: initialOffer?.benefitsJson || '',
    negotiationNotes: initialOffer?.negotiationNotes || '',
  }));
  const [submitting, setSubmitting] = useState(false);
  // FE-25 — "kirli form" kapısı. Kadro talebinden gelen ÖN DOLDURMA (setForm) bunu
  // tetiklemez; yalnız KULLANICININ yazdığı bir değer diyaloğu kirli sayar. Aksi
  // halde hiç dokunulmamış bir formu ESC ile kapatmak da onay sorardı.
  const [touched, setTouched] = useState(false);
  const [formError, setFormError] = useState(null);
  const [pastStartConfirm, setPastStartConfirm] = useState(false);

  const setField = (k, v) => {
    setTouched(true);
    setFormError(null);
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  // Diyalog her açılışta kadro talebini tazeler ve para birimi / sözleşme tipini ÖN DOLDURUR.
  // api.js GET'te REJECT ETMEZ — gerçek sinyal `res.data.status` (repo hafızası
  // `reference_api_interceptor_never_rejects_reads`); "yüklenemedi" sessizce "bant yok"a
  // dönüşmesin diye durum açıkça kontrol edilir.
  useEffect(() => {
    if (!open) { setRequisition(null); return; }
    setTouched(false);
    setFormError(null);
    setPastStartConfirm(false);
    const requisitionId = application?.requisitionId;
    if (!requisitionId) { setRequisition(null); return; }
    let cancelled = false;
    api.get(`/v1/ats-requisitions/${requisitionId}`)
      .then((res) => {
        if (cancelled || !res?.data?.status) return;
        const r = res.data.data;
        if (!r) return;
        setRequisition(r);
        // Revizyonda ön doldurma YAPILMAZ: kullanıcının mevcut teklifteki seçimi kadronun
        // varsayılanıyla sessizce değiştirilirse revizyon "gördüğünden başka bir şey" gönderir.
        if (isRevise) return;
        setForm((prev) => ({
          ...prev,
          currency: r.salaryCurrency || prev.currency,
          contractType: CONTRACT_TYPES_KEYS.includes(r.contractType) ? r.contractType : prev.contractType,
        }));
      })
      .catch(() => { /* bant gösterilmez; teklif yazımı engellenmez */ });
    return () => { cancelled = true; };
  }, [open, application?.requisitionId, isRevise]);

  // Bant durumu — yüklem BACKEND'in ComputeBandViolationAsync'iyle BİREBİR aynı sırada:
  // önce PARA BİRİMİ (60-90k TRY bandına 70.000 USD "sığmaz" — birim DÖNÜŞÜMÜ YOK),
  // sonra alt/üst sınır. Arayüz backend'in söyleyeceğinden başka bir şey söylememeli.
  const band = useMemo(() => {
    if (!requisition) return null;
    const { minSalary, maxSalary, salaryCurrency } = requisition;
    if (minSalary == null && maxSalary == null && !salaryCurrency) return null;

    const amount = Number(form.baseSalary);
    const hasAmount = form.baseSalary !== '' && Number.isFinite(amount);
    let reason = null;
    if (salaryCurrency && form.currency && salaryCurrency !== form.currency) reason = 'currency_mismatch';
    else if (hasAmount && minSalary != null && amount < minSalary) reason = 'below_min';
    else if (hasAmount && maxSalary != null && amount > maxSalary) reason = 'above_max';

    const lang = i18n.language;
    const range = minSalary != null && maxSalary != null
      ? `${formatAmount(minSalary, lang)} – ${formatAmount(maxSalary, lang)}`
      : minSalary != null
        ? `≥ ${formatAmount(minSalary, lang)}`
        : maxSalary != null ? `≤ ${formatAmount(maxSalary, lang)}` : '';
    return { reason, label: [range, salaryCurrency || ''].filter(Boolean).join(' ').trim() };
  }, [requisition, form.baseSalary, form.currency, i18n.language]);

  const contractOptions = CONTRACT_TYPES_KEYS.map((k) => ({
    id: k,
    name: t(`offer.contractType.${k}`, { defaultValue: k }),
  }));

  // "yyyy-MM-dd" string for today (matches DatePicker emit format) — used as
  // the hard floor for ResponseDeadline (DatePicker minDate) and the soft
  // floor for StartDate (we just confirm with the user).
  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  // `skipPastStartCheck` yalnız geçmiş-tarih onay diyaloğundan "Evet" dönünce true olur.
  const handleSubmit = async (skipPastStartCheck = false) => {
    setFormError(null);
    const baseSalaryNum = Number(form.baseSalary);
    if (!Number.isFinite(baseSalaryNum) || baseSalaryNum <= 0) {
      setFormError(t('offer.create.baseSalaryMustBePositive'));
      return;
    }
    if (!application?.id) return;

    // Date sequencing: ResponseDeadline must not be later than StartDate.
    // Compare as plain "yyyy-MM-dd" strings — they sort lexically.
    if (form.startDate && form.responseDeadline
        && form.responseDeadline > form.startDate) {
      setFormError(t('offer.create.responseDeadlineAfterStartDate'));
      return;
    }

    // Past-date guard: ResponseDeadline cannot precede today.
    if (form.responseDeadline && form.responseDeadline < today) {
      setFormError(t('offer.create.responseDeadlineInPast'));
      return;
    }

    // Soft warning: StartDate in the past is uncommon (back-dated hires)
    // but legitimate — confirm with the user before sending.
    // FE-40 — eskiden `window.confirm`di: tarayıcının yerel penceresi diyaloğun
    // odak tuzağını tamamen delip geçiyor, ev tasarım dilinin dışında görünüyor ve
    // bazı gömülü tarayıcılarda hiç çıkmıyordu. Artık evin `ConfirmDialog`'u.
    if (!skipPastStartCheck && form.startDate && form.startDate < today) {
      setPastStartConfirm(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        applicationId: application.id,
        baseSalary: baseSalaryNum,
        currency: form.currency || 'TRY',
        bonusCash: form.bonusCash !== '' ? Number(form.bonusCash) : null,
        bonusEquity: form.bonusEquity !== '' ? Number(form.bonusEquity) : null,
        startDate: form.startDate || null,
        responseDeadline: form.responseDeadline || null,
        contractType: form.contractType || null,
        contractDurationMonths: form.contractDurationMonths !== '' ? Number(form.contractDurationMonths) : null,
        benefitsJson: form.benefitsJson || null,
        negotiationNotes: form.negotiationNotes || null,
      };
      const res = isRevise
        ? await api.post(`/v1/ats-offers/${offerId}/revise`, payload)
        : await api.post('/v1/ats-offers', payload);
      if (res.data?.status) {
        onCreated?.(res.data.data);
        onClose?.();
      }
    } catch {
      // interceptor already toasted
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <A11yDialog
      open={open}
      onClose={onClose}
      dirty={touched}
      overlay={Overlay}
      card={Card}
      overlayProps={{ $open: true }}
    >
      {({ titleId }) => (
      <>
        <Title id={titleId}>{isRevise ? t('offer.revise.title') : t('offer.create.title')}</Title>
        <Subtitle>
          {t('offer.create.subtitle', {
            candidate: application?.candidateFullName || '',
            jobTitle: application?.jobPostingTitle || '',
          })}
        </Subtitle>

        <Grid>
          <TextInput
            label={t('offer.field.baseSalary')}
            type="number"
            value={form.baseSalary}
            onChange={(e) => setField('baseSalary', e.target.value)}
            required
          />
          <GenericSelect
            label={t('offer.field.currency')}
            options={CURRENCIES}
            value={form.currency}
            onChange={(id) => setField('currency', id || 'TRY')}
            required
          />
        </Grid>
        {band && band.label && (
          <BandLine $tone={band.reason ? 'warn' : undefined} data-testid="offer-band-line">
            {band.reason
              ? `${t('offer.band.label', { range: band.label })} — ${t(`offer.band.${band.reason}`)}`
              : t('offer.band.label', { range: band.label })}
          </BandLine>
        )}
        <Grid>
          <TextInput
            label={t('offer.field.bonusCash')}
            type="number"
            value={form.bonusCash}
            onChange={(e) => setField('bonusCash', e.target.value)}
          />
          <TextInput
            label={t('offer.field.bonusEquity')}
            type="number"
            value={form.bonusEquity}
            onChange={(e) => setField('bonusEquity', e.target.value)}
          />
        </Grid>
        <Grid>
          <DateTimePicker
            label={t('offer.field.startDate')}
            mode="date"
            value={form.startDate}
            onChange={(v) => setField('startDate', v || '')}
          />
          <DateTimePicker
            label={t('offer.field.responseDeadline')}
            mode="date"
            value={form.responseDeadline}
            onChange={(v) => setField('responseDeadline', v || '')}
            minDate={today}
          />
        </Grid>
        <Grid>
          <GenericSelect
            label={t('offer.field.contractType')}
            options={contractOptions}
            value={form.contractType}
            onChange={(id) => setField('contractType', id || '')}
          />
          {form.contractType === 'fixed_term' && (
            <TextInput
              label={t('offer.field.contractDurationMonths')}
              type="number"
              value={form.contractDurationMonths}
              onChange={(e) => setField('contractDurationMonths', e.target.value)}
            />
          )}
        </Grid>
        <FullRow>
          <TextInput
            label={t('offer.field.benefitsJson')}
            type="textarea"
            rows={3}
            value={form.benefitsJson}
            onChange={(e) => setField('benefitsJson', e.target.value)}
          />
        </FullRow>
        <FullRow>
          <TextInput
            label={t('offer.field.negotiationNotes')}
            type="textarea"
            rows={3}
            value={form.negotiationNotes}
            onChange={(e) => setField('negotiationNotes', e.target.value)}
          />
        </FullRow>

        {formError && <DialogErrorLine role="alert" data-testid="offer-form-error">{formError}</DialogErrorLine>}

        <Actions>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => handleSubmit()} loading={submitting} disabled={submitting || !form.baseSalary || Number(form.baseSalary) <= 0}>
            {isRevise ? t('offer.revise.submit') : t('offer.create.submit')}
          </Button>
        </Actions>
      </>
      )}
    </A11yDialog>
    <ConfirmDialog
      isOpen={pastStartConfirm}
      title={t('offer.field.startDate')}
      message={t('offer.startDateInPastConfirm')}
      confirmText={t('common.confirm')}
      onConfirm={() => { setPastStartConfirm(false); handleSubmit(true); }}
      onCancel={() => setPastStartConfirm(false)}
      type="primary"
    />
    </>
  );
}
